/**
 * RecordersList — grabadores de vídeo (NVR/DVR).
 *
 * Listado estándar + **ficha de pantalla completa** (`RecorderForm`): pestañas
 * General, Ubicación (rack y U), Cámaras y Contactos. Ya no es un modal.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ServerStackIcon, PlusIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';
import ListPage from './common/ListPage';
import RecorderForm from './RecorderForm';

const ESTADOS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decommissioned', label: 'Dado de baja' },
];

export default function RecordersList({ companyId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [hlId, setHlId] = useState(null);

  // Ficha abierta: { recorder|null, mode }
  const [ficha, setFicha] = useState(null);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [grabadores, camaras, ubicaciones, racksRes] = await Promise.all([
        api.getRecorders(companyId),
        api.getCameras(companyId),
        api.getLocations('', companyId),
        api.getRacks(undefined, companyId, false, true).catch(() => ({ data: [] })),
      ]);
      setItems(grabadores.data || []);
      setCameras(camaras.data || []);
      setLocations(ubicaciones.data || []);
      setRacks(racksRes.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los grabadores');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find((item) => item.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setFicha({ recorder: target, mode: 'view' }); return undefined; }
    setHlId(id);
    const row = document.getElementById(`recorder-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  const ubicacionNombre = (id) => locations.find((loc) => loc.id === Number(id))?.name || '';

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const coincide = !q
        || (item.name || '').toLowerCase().includes(q)
        || (item.code || '').toLowerCase().includes(q)
        || (item.model || '').toLowerCase().includes(q)
        || (item.ip_address || '').toLowerCase().includes(q)
        || (item.serial_number || '').toLowerCase().includes(q);
      const estado = !filtroEstado || item.status === filtroEstado;
      return coincide && estado;
    });
  }, [items, search, filtroEstado]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navRecorders = useNavOrder(filtrados, ficha?.recorder || null, Boolean(ficha));

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Grabador',
        sortable: true,
        render: (item) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <ServerStackIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
            <strong>{item.name}</strong>
          </span>
        ),
      },
      { key: 'code', label: 'Código', sortable: true, render: (item) => (item.code ? <span className="list-code">{item.code}</span> : dash) },
      { key: 'model', label: 'Modelo', sortable: true, render: (item) => item.model || dash },
      {
        key: 'ip_address',
        label: 'IP',
        sortable: true,
        render: (item) => (item.ip_address ? <span className="list-code">{item.ip_address}</span> : dash),
      },
      {
        key: 'camera_count',
        label: 'Cámaras',
        sortable: true,
        render: (item) => (
          <span className={`list-badge ${item.camera_count ? 'list-badge--accent' : 'list-badge--muted'}`}>
            <VideoCameraIcon style={{ width: 12, height: 12 }} /> {item.camera_count || 0}
          </span>
        ),
      },
      { key: 'channels', label: 'Canales', sortable: true, render: (item) => item.channels || dash },
      {
        key: 'rack_name',
        label: 'Rack',
        sortable: true,
        render: (item) => (item.rack_name
          ? <span className="list-badge">{item.rack_name}{item.position_u ? ` · U${item.position_u}` : ''}</span>
          : dash),
      },
      {
        key: 'location_name',
        label: 'Ubicación',
        sortable: true,
        sortValue: (item) => ubicacionNombre(item.location_id) || item.location_name || '',
        render: (item) => ubicacionNombre(item.location_id) || item.location_name || dash,
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        render: (item) => (
          <span className={`list-badge ${
            item.status === 'active' ? 'list-badge--on'
              : item.status === 'maintenance' ? 'list-badge--warn'
                : item.status === 'decommissioned' ? 'list-badge--muted' : 'list-badge--off'
          }`}>
            {item.status_label || item.status}
          </span>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const exportRows = useMemo(
    () => filtrados.map((item) => ({
      Nombre: item.name,
      Código: item.code || '',
      Fabricante: item.manufacturer || '',
      Modelo: item.model || '',
      IP: item.ip_address || '',
      Cámaras: item.camera_count || 0,
      Canales: item.channels || '',
      Rack: item.rack_name || '',
      'U inicial': item.position_u || '',
      Ubicación: ubicacionNombre(item.location_id) || item.location_name || '',
      Estado: item.status_label || item.status,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtrados, locations],
  );

  if (ficha) {
    return (
      <RecorderForm
        recorder={ficha.recorder}
        companyId={companyId}
        recorders={items}
        cameras={cameras}
        locations={locations}
        racks={racks}
        initialMode={ficha.mode}
        navItems={navRecorders}
        navIndex={ficha.recorder?.id ? navRecorders.findIndex(r => String(r.id) === String(ficha.recorder.id)) : -1}
        onNavigate={(destino) => setFicha({ recorder: destino, mode: ficha.mode })}
        onClose={() => setFicha(null)}
        onSaved={() => { cargar(); }}
        onDeleted={() => { setFicha(null); cargar(); }}
      />
    );
  }

  return (
    <ListPage
      icon={ServerStackIcon}
      title="Grabadores"
      subtitle="NVR/DVR: IP de gestión, montaje en rack y las cámaras que cuelgan de cada uno."
      companyId={companyId}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar grabador, IP, modelo…"
      filters={[
        {
          key: 'status',
          label: 'Estado',
          allLabel: 'Todos los estados',
          value: filtroEstado,
          onChange: setFiltroEstado,
          options: ESTADOS,
        },
      ]}
      columns={columns}
      columnStorageKey="cmdb_cols_recorders"
      listKey="recorders"
      rows={filtrados}
      rowId={(item) => `recorder-row-${item.id}`}
      rowClassName={(item) => (item.id === hlId ? 'list-row-highlight' : '')}
      onRowClick={(item) => setFicha({ recorder: item, mode: 'view' })}
      exportRows={exportRows}
      exportFileBase="grabadores"
      loading={loading && items.length === 0}
      error={error}
      onCreate={() => setFicha({ recorder: null, mode: 'create' })}
      createLabel="Nuevo grabador"
      importEntity="recorders"
      onImported={cargar}
      empty={{
        icon: '🎞️',
        title: items.length === 0 ? 'Todavía no hay grabadores' : 'Ningún grabador coincide',
        subtitle: items.length === 0
          ? 'Da de alta el NVR/DVR y luego sus cámaras en «Cámaras».'
          : undefined,
        action: items.length === 0 ? (
          <button type="button" className="list-btn-primary" onClick={() => setFicha({ recorder: null, mode: 'create' })}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo grabador
          </button>
        ) : undefined,
      }}
      actions={{
        onEdit: (row) => setFicha({ recorder: row, mode: 'edit' }),
        onDelete: async (row) => { await api.deleteRecorder(row.id, companyId); await cargar(); },
        deleteTitle: 'Eliminar grabador',
        deleteMessage: (row) => `¿Eliminar el grabador «${row.name}»? Sus ${row.camera_count || 0} cámara(s) se quedan sin grabador; también se desmonta del rack.`,
      }}
    />
  );
}
